// src/components/Tools/Results/IpsResults.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultRendererProps } from './types';

type Sources = {
  theHarvester?: { ips?: string[]; emails?: string[] };
  dig?: { ips?: string[] };
  host?: { ips?: string[] };
};
type Payload = {
  domain?: string;
  ips?: string[];
  sources?: Sources;
  partial?: boolean;
  errors?: Record<string, string>;
};

function normalize(results: any[]): Payload {
  if (!Array.isArray(results) || results.length === 0) return { domain: '', ips: [], sources: {} };
  const first = results[0];
  if (first && typeof first === 'object') return first as Payload;
  return { domain: '', ips: results as string[] };
}

const IpsResults: React.FC<ResultRendererProps> = ({ results }) => {
  const { t } = useTranslation();
  const payload = React.useMemo(() => normalize(results), [results]);

  const ips = Array.isArray(payload.ips) ? payload.ips : [];
  const domain = payload.domain || '';

  if (ips.length === 0) {
    return <p className="text-sm text-slate-500">{t('tools.results_scan.ips.empty')}</p>;
  }

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(ips.join('\n'));
    } catch {}
  };

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-3xl space-y-6">
        <div className="text-center space-y-1">
          <h3 className="text-2xl font-bold text-slate-900">{t('tools.results_scan.ips.title')}</h3>
          <p className="text-sm text-slate-500">
            {domain ? `${domain} • ` : ''}{t('tools.results_scan.ips.found_count', { count: ips.length })}
          </p>
          <button
            type="button"
            onClick={copyAll}
            className="mt-1 inline-flex items-center rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            {t('tools.results_scan.ips.copy')}
          </button>
          {payload.partial && (
            <div className="mt-2 text-xs text-amber-700 bg-amber-50 inline-flex rounded px-2 py-0.5">
              {t('tools.results_scan.ips.partial')}
            </div>
          )}
        </div>

        {/* Lista de IPs */}
        <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white">
          <div className="max-h-72 overflow-auto">
            <ul className="divide-y divide-slate-200 text-sm">
              {ips.map((ip, i) => (
                <li key={i} className="px-4 py-2 font-mono text-slate-900">{ip}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Detalle por fuente (opcional) */}
        {payload.sources && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(['dig','host','theHarvester'] as const).map(src => {
              const s = payload.sources?.[src];
              if (!s) return null;
              const list = s.ips || [];
              const emails = (s as any).emails || [];
              return (
                <div key={src} className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="text-sm font-semibold text-slate-800 mb-2">
                    {t(`tools.results_scan.ips.source.${src}`)}
                  </div>
                  <div className="text-xs text-slate-500 mb-1">{t('tools.results_scan.ips.count', { count: list.length })}</div>
                  <div className="text-xs font-mono text-slate-900 whitespace-pre-wrap break-all">
                    {list.slice(0, 10).join('\n')}
                    {list.length > 10 ? '\n…' : ''}
                  </div>
                  {emails.length > 0 && (
                    <div className="mt-3">
                      <div className="text-xs text-slate-500">{t('tools.results_scan.ips.emails')} ({emails.length})</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default IpsResults;
